import React, { useEffect, useState } from 'react';
import { Eye, Copy, Download, Plus, Server, ShieldOff, CheckCircle, XCircle, Clock } from 'lucide-react';
import toast from 'react-hot-toast';
import { API_URL } from '../config';

// Keep in sync with backend DEFAULT_FEATURES and nxtVault NAS
// middlewared/utils/license_features.py.
const FEATURES = [
  'DEDUP',
  'JAILS',
  'FIBRECHANNEL',
  'VM',
  'SED',
  'REPLICATION',
  'CLOUD_BACKUP',
];
const STATUS_OPTIONS = ['ALL', 'ACTIVE', 'REVOKED', 'EXPIRED'];
const SERIAL_RE = /^XNAS-[A-Z0-9-]{1,32}$/i;

// Generate an 8-hex-char slice for both halves of an HA pair. The DB
// has a UNIQUE index per node serial, so the slice MUST be random --
// falling back to a constant (e.g. all zeros) would collide on every
// re-issue after the first.
const generateSerialPair = () => {
  let slice;
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    slice = crypto.randomUUID().replace(/-/g, '').slice(0, 8).toUpperCase();
  } else if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    // Wider-support fallback: 4 random bytes -> 8 hex chars.
    const buf = new Uint8Array(4);
    crypto.getRandomValues(buf);
    slice = Array.from(buf, (b) => b.toString(16).padStart(2, '0')).join('').toUpperCase();
  } else {
    // Last resort: timestamp + Math.random. Not cryptographic, but at
    // least varies per call so we don't collide with ourselves.
    const t = Date.now().toString(16);
    const r = Math.floor(Math.random() * 0xffff).toString(16).padStart(4, '0');
    slice = (t + r).slice(-8).toUpperCase();
  }
  return {
    nodeASerial: `XNAS-${slice}-A`,
    nodeBSerial: `XNAS-${slice}-B`,
  };
};

const initialForm = () => ({
  customerName: '',
  customerKey: '',
  ...generateSerialPair(),
  validityDays: 365,
  contractType: 'gold',
  features: [...FEATURES],
  notes: '',
  empReferenceCode: '',
  hwBind: false,
  bindMode: 'strict',
  nodeASystemUuid: '',
  nodeASystemSerial: '',
  nodeBSystemUuid: '',
  nodeBSystemSerial: '',
});

const AdminNxtvaultLicenses = () => {
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [search, setSearch] = useState('');
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.title = 'nxtVault NAS Licenses | Admin Portal';
  }, []);

  useEffect(() => {
    fetchLicenses();
  }, [filterStatus]);

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  });

  const fetchLicenses = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus && filterStatus !== 'ALL') params.set('status', filterStatus);
      if (search) params.set('search', search);
      const url = `${API_URL}/nxtvault-licenses${params.toString() ? `?${params}` : ''}`;
      const res = await fetch(url, { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data?.error || 'Failed to load nxtVault NAS licenses');
      }
      setLicenses(data.items || []);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const openIssueModal = () => {
    setForm(initialForm());
    setShowIssueModal(true);
  };

  const updateForm = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleFeature = (f) =>
    setForm((prev) => ({
      ...prev,
      features: prev.features.includes(f)
        ? prev.features.filter((x) => x !== f)
        : [...prev.features, f],
    }));

  const submitIssue = async () => {
    if (!form.customerName.trim()) {
      toast.error('Customer name is required');
      return;
    }
    if (!SERIAL_RE.test(form.nodeASerial) || !SERIAL_RE.test(form.nodeBSerial)) {
      toast.error('Node serials must look like "XNAS-..."');
      return;
    }
    if (form.nodeASerial.toUpperCase() === form.nodeBSerial.toUpperCase()) {
      toast.error('Node A and Node B serials must differ');
      return;
    }
    const days = parseInt(form.validityDays, 10);
    if (!Number.isFinite(days) || days < 1) {
      toast.error('Validity must be a positive number of days');
      return;
    }
    if (form.features.length === 0) {
      toast.error('Select at least one feature');
      return;
    }

    if (
      !window.confirm(
        'Issuing a nxtVault NAS HA license is irrevocable -- the signed blobs cannot be modified after issue. Continue?'
      )
    ) {
      return;
    }

    let nodeAHardware = null;
    let nodeBHardware = null;
    if (form.hwBind) {
      const aUuid = form.nodeASystemUuid.trim();
      const aSerial = form.nodeASystemSerial.trim();
      const bUuid = form.nodeBSystemUuid.trim();
      const bSerial = form.nodeBSystemSerial.trim();
      if (!(aUuid || aSerial) || !(bUuid || bSerial)) {
        toast.error('Hardware binding needs at least system_uuid OR system_serial for each node');
        return;
      }
      nodeAHardware = {
        bind_mode: form.bindMode,
        ...(aUuid ? { system_uuid: aUuid } : {}),
        ...(aSerial ? { system_serial: aSerial } : {}),
      };
      nodeBHardware = {
        bind_mode: form.bindMode,
        ...(bUuid ? { system_uuid: bUuid } : {}),
        ...(bSerial ? { system_serial: bSerial } : {}),
      };
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/nxtvault-licenses`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          validityDays: days,
          nodeAHardware,
          nodeBHardware,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data?.error || 'Failed to issue license');
      }
      toast.success('License issued. Download both node blobs from the details view.');
      setShowIssueModal(false);
      await fetchLicenses();
      viewDetails(data.license.id);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const viewDetails = async (id) => {
    try {
      const res = await fetch(`${API_URL}/nxtvault-licenses/${id}`, { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data?.error || 'Failed to load details');
      }
      setSelected(data);
      setShowDetailsModal(true);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const downloadNode = async (id, node, serial) => {
    try {
      const res = await fetch(`${API_URL}/nxtvault-licenses/${id}/download/${node}`, {
        headers: authHeaders(),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Download failed: ${text}`);
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `nxtvault-${serial}.xlic`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const copyNode = async (blob, node) => {
    try {
      if (!blob) throw new Error('License blob is unavailable');
      await navigator.clipboard.writeText(blob);
      toast.success(`Node ${node} license copied`);
    } catch (e) {
      toast.error('Could not copy license. Download the node license file instead.');
    }
  };

  const revokeLicense = async (id) => {
    const reason = window.prompt('Reason for revocation (optional):', '');
    if (reason === null) return;
    try {
      const res = await fetch(`${API_URL}/nxtvault-licenses/${id}/revoke`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data?.error || 'Failed to revoke');
      }
      toast.success('License revoked');
      await fetchLicenses();
      if (showDetailsModal && selected?.license?.id === id) {
        viewDetails(id);
      }
    } catch (e) {
      toast.error(e.message);
    }
  };

  const statusBadge = (status) => {
    const cfg = {
      ACTIVE: { icon: CheckCircle, className: 'bg-green-100 text-green-800', label: 'Active' },
      REVOKED: { icon: XCircle, className: 'bg-red-100 text-red-800', label: 'Revoked' },
      EXPIRED: { icon: Clock, className: 'bg-gray-200 text-gray-800', label: 'Expired' },
    }[status] || { icon: Clock, className: 'bg-gray-100 text-gray-700', label: status };
    const Icon = cfg.icon;
    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${cfg.className}`}
      >
        <Icon className="w-3 h-3 mr-1" />
        {cfg.label}
      </span>
    );
  };

  const fmtDate = (d) => (d ? new Date(d).toLocaleString() : '-');

  if (loading && licenses.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500 text-sm">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">nxtVault NAS Licenses</h1>
          <p className="mt-1 text-sm text-gray-500">
            Issue and manage signed nxtVault NAS HA license pairs by nxtSIGHT Technologies Pvt Ltd
          </p>
        </div>
        <button
          onClick={openIssueModal}
          style={{ backgroundColor: '#197560' }}
          className="inline-flex items-center gap-2 text-white px-4 py-2 rounded-md text-sm font-medium hover:opacity-90"
        >
          <Plus className="w-4 h-4" /> Issue HA License
        </button>
      </div>

      {/* Filter Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-2">
        <div className="flex space-x-2">
          {STATUS_OPTIONS.map((status) => (
            <button
              key={status}
              onClick={() => setFilterStatus(status)}
              style={filterStatus === status ? { backgroundColor: '#197560' } : {}}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                filterStatus === status
                  ? 'text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {status}
            </button>
          ))}
        </div>
      </div>

      {/* Search */}
      <div className="flex items-center gap-2">
        <input
          type="text"
          placeholder="Search customer / serial..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && fetchLicenses()}
          className="flex-1 border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
        />
        <button
          onClick={fetchLicenses}
          className="px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
        >
          Search
        </button>
      </div>

      {/* nxtVault NAS Licenses Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {licenses.length === 0 ? (
          <div className="p-6 text-center text-gray-500 text-sm">
            <Server className="mx-auto h-10 w-10 text-gray-400 mb-2" />
            <p>No nxtVault NAS licenses found. Click <span className="font-semibold">Issue HA License</span> to create one.</p>
          </div>
        ) : (
        <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Customer</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Node A</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Node B</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Tier</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Validity</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Status</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Issued</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {licenses.map((l) => (
              <tr key={l.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm">
                  <div className="font-medium text-gray-900">{l.customerName}</div>
                  {l.customerKey && (
                    <div className="text-xs text-gray-500">{l.customerKey}</div>
                  )}
                </td>
                <td className="px-4 py-3 text-xs font-mono text-gray-700">{l.nodeASerial}</td>
                <td className="px-4 py-3 text-xs font-mono text-gray-700">{l.nodeBSerial}</td>
                <td className="px-4 py-3 text-sm text-gray-700 capitalize">{l.contractType}</td>
                <td className="px-4 py-3 text-xs text-gray-500">
                  {fmtDate(l.notBefore)} → {fmtDate(l.notAfter)}
                </td>
                <td className="px-4 py-3">{statusBadge(l.status)}</td>
                <td className="px-4 py-3 text-xs text-gray-500">{fmtDate(l.issuedAt)}</td>
                <td className="px-4 py-3 text-right space-x-2">
                  <button
                    onClick={() => viewDetails(l.id)}
                    className="text-blue-600 hover:text-blue-800"
                    title="Details"
                  >
                    <Eye className="w-4 h-4 inline" />
                  </button>
                  {l.status === 'ACTIVE' && (
                    <button
                      onClick={() => revokeLicense(l.id)}
                      className="text-red-600 hover:text-red-800"
                      title="Revoke"
                    >
                      <ShieldOff className="w-4 h-4 inline" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        )}
      </div>

      {showIssueModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-semibold">Issue nxtVault NAS HA License</h2>
              <button
                onClick={() => setShowIssueModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-gray-700">Customer name *</label>
                  <input
                    value={form.customerName}
                    onChange={(e) => updateForm('customerName', e.target.value)}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-700">Customer key</label>
                  <input
                    value={form.customerKey}
                    onChange={(e) => updateForm('customerKey', e.target.value)}
                    placeholder="ACME-12345"
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-medium text-gray-700">Node A serial *</label>
                    <button
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, ...generateSerialPair() }))}
                      className="text-xs text-blue-600 hover:text-blue-800"
                      title="Regenerate auto-serials for both nodes"
                    >
                      Regenerate
                    </button>
                  </div>
                  <input
                    value={form.nodeASerial}
                    onChange={(e) => updateForm('nodeASerial', e.target.value.toUpperCase())}
                    placeholder="XNAS-NODEA-LAB01"
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-700">Node B serial *</label>
                  <input
                    value={form.nodeBSerial}
                    onChange={(e) => updateForm('nodeBSerial', e.target.value.toUpperCase())}
                    placeholder="XNAS-NODEB-LAB01"
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-700">Validity (days) *</label>
                  <input
                    type="number"
                    min={1}
                    value={form.validityDays}
                    onChange={(e) => updateForm('validityDays', e.target.value)}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-700">Contract tier</label>
                  <select
                    value={form.contractType}
                    onChange={(e) => updateForm('contractType', e.target.value)}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  >
                    <option value="bronze">Bronze</option>
                    <option value="silver">Silver</option>
                    <option value="gold">Gold</option>
                    <option value="platinum">Platinum</option>
                  </select>
                </div>
              </div>

              <p className="text-xs text-gray-500">
                License serials keep the XNAS- prefix for appliance compatibility.
              </p>

              <div>
                <label className="text-xs font-medium text-gray-700 block mb-1">Features</label>
                <div className="flex flex-wrap gap-2">
                  {FEATURES.map((f) => (
                    <button
                      type="button"
                      key={f}
                      onClick={() => toggleFeature(f)}
                      className={`px-3 py-1 rounded text-xs border ${
                        form.features.includes(f)
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-300'
                      }`}
                    >
                      {f}
                    </button>
                  ))}
                </div>
              </div>

              <div className="border border-gray-200 rounded p-3 space-y-3">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-800">
                  <input
                    type="checkbox"
                    checked={form.hwBind}
                    onChange={(e) => updateForm('hwBind', e.target.checked)}
                  />
                  Bind license to specific hardware (optional)
                </label>
                <p className="text-xs text-gray-500">
                  When enabled, the nxtVault NAS middleware verifies the running system&apos;s DMI
                  identifiers against the values below. Providing a system UUID also encrypts
                  that node&apos;s license so only the matching appliance can open it.
                  Leave unchecked for VM / generic hardware deployments.
                </p>

                {form.hwBind && (
                  <>
                    <div>
                      <label className="text-xs font-medium text-gray-700">Bind mode</label>
                      <select
                        value={form.bindMode}
                        onChange={(e) => updateForm('bindMode', e.target.value)}
                        className="w-full md:w-1/2 border border-gray-300 rounded px-3 py-2 text-sm"
                      >
                        <option value="strict">Strict (reject mismatches)</option>
                        <option value="warn">Warn (log mismatches, license still valid)</option>
                      </select>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs font-medium text-gray-700">Node A — system UUID</label>
                        <input
                          value={form.nodeASystemUuid}
                          onChange={(e) => updateForm('nodeASystemUuid', e.target.value)}
                          placeholder="dmidecode -s system-uuid"
                          className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-700">Node A — chassis serial</label>
                        <input
                          value={form.nodeASystemSerial}
                          onChange={(e) => updateForm('nodeASystemSerial', e.target.value)}
                          placeholder="dmidecode -s system-serial-number"
                          className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-700">Node B — system UUID</label>
                        <input
                          value={form.nodeBSystemUuid}
                          onChange={(e) => updateForm('nodeBSystemUuid', e.target.value)}
                          className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-700">Node B — chassis serial</label>
                        <input
                          value={form.nodeBSystemSerial}
                          onChange={(e) => updateForm('nodeBSystemSerial', e.target.value)}
                          className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono"
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-gray-700">Employee reference</label>
                  <input
                    value={form.empReferenceCode}
                    onChange={(e) => updateForm('empReferenceCode', e.target.value)}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-700">Notes</label>
                  <input
                    value={form.notes}
                    onChange={(e) => updateForm('notes', e.target.value)}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  />
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t flex justify-end gap-2">
              <button
                onClick={() => setShowIssueModal(false)}
                className="px-4 py-2 border border-gray-300 rounded text-sm hover:bg-gray-50"
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                onClick={submitIssue}
                disabled={submitting}
                className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50"
              >
                {submitting ? 'Signing...' : 'Issue & Sign'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDetailsModal && selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-semibold">License Details</h2>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <div className="p-6 space-y-5">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-gray-500">Customer:</span> {selected.license.customerName}</div>
                <div><span className="text-gray-500">Status:</span> {statusBadge(selected.license.status)}</div>
                <div className="font-mono text-xs"><span className="text-gray-500 font-sans">Node A:</span> {selected.license.nodeASerial}</div>
                <div className="font-mono text-xs"><span className="text-gray-500 font-sans">Node B:</span> {selected.license.nodeBSerial}</div>
                <div><span className="text-gray-500">Issued:</span> {fmtDate(selected.license.issuedAt)}</div>
                <div><span className="text-gray-500">Expires:</span> {fmtDate(selected.license.notAfter)}</div>
                <div><span className="text-gray-500">Tier:</span> <span className="capitalize">{selected.license.contractType}</span></div>
                <div><span className="text-gray-500">Issued by:</span> {selected.license.issuedBy?.email || '-'}</div>
                {selected.license.revokedAt && (
                  <>
                    <div><span className="text-gray-500">Revoked:</span> {fmtDate(selected.license.revokedAt)}</div>
                    <div><span className="text-gray-500">By:</span> {selected.license.revokedBy?.email || '-'}</div>
                  </>
                )}
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => downloadNode(selected.license.id, 'A', selected.license.nodeASerial)}
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-green-600 text-white px-4 py-2 rounded text-sm hover:bg-green-700"
                >
                  <Download className="w-4 h-4" /> Download nodeA.xlic
                </button>
                <button
                  onClick={() => downloadNode(selected.license.id, 'B', selected.license.nodeBSerial)}
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-green-600 text-white px-4 py-2 rounded text-sm hover:bg-green-700"
                >
                  <Download className="w-4 h-4" /> Download nodeB.xlic
                </button>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => copyNode(selected.license.nodeAJws, 'A')}
                  className="flex-1 inline-flex items-center justify-center gap-2 border border-gray-300 text-gray-700 px-4 py-2 rounded text-sm hover:bg-gray-50"
                >
                  <Copy className="w-4 h-4" /> Copy Node A license
                </button>
                <button
                  onClick={() => copyNode(selected.license.nodeBJws, 'B')}
                  className="flex-1 inline-flex items-center justify-center gap-2 border border-gray-300 text-gray-700 px-4 py-2 rounded text-sm hover:bg-gray-50"
                >
                  <Copy className="w-4 h-4" /> Copy Node B license
                </button>
              </div>

              {(() => {
                const blob = selected.license?.nodeAJws || '';
                const isEnvelope = blob.startsWith('XNAS-LIC1:');
                if (isEnvelope) {
                  return (
                    <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-800">
                      <div className="font-medium mb-1">Hardware-bound (encrypted envelope)</div>
                      This license is delivered as an opaque <code>XNAS-LIC1:</code> envelope.
                      The payload contents are AES-256-GCM encrypted with a key derived from
                      the target hardware&apos;s DMI <code>system_uuid</code> and cannot be decoded
                      from the portal. Only the matching nxtVault NAS host can decrypt it.
                    </div>
                  );
                }
                if (selected.decodedPayload) {
                  return (
                    <details className="text-xs">
                      <summary className="cursor-pointer text-gray-600 font-medium">
                        Show decoded payload (Node A blob, technical)
                      </summary>
                      <pre className="bg-gray-900 text-gray-100 text-xs p-3 rounded overflow-x-auto mt-2">
                        {JSON.stringify(selected.decodedPayload, null, 2)}
                      </pre>
                    </details>
                  );
                }
                return null;
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminNxtvaultLicenses;
